import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DEFAULT_CURRENCY } from '../constants/currencies';

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export const CurrencyProvider = ({ children }) => {
  // Initialize display currency from localStorage or use default
  const [displayCurrency, setDisplayCurrency] = useState(() => {
    const saved = localStorage.getItem('displayCurrency');
    return saved || DEFAULT_CURRENCY;
  });
  const [planBaseCurrency, setPlanBaseCurrency] = useState(DEFAULT_CURRENCY);
  const [exchangeRates, setExchangeRates] = useState({});
  const [loading, setLoading] = useState(false);
  const [lastFetched, setLastFetched] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // Fetch exchange rates from backend
  const fetchExchangeRates = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/multi-currency/rates`);
      
      if (response.data && response.data.rates) {
        setExchangeRates(response.data.rates);
        setLastFetched(new Date());
        console.log('[CURRENCY] Exchange rates fetched successfully');
      }
    } catch (error) {
      console.error('[CURRENCY] Error fetching exchange rates:', error);
      // Set fallback rates if API fails (updated Dec 2025)
      setExchangeRates({
        USD: 1.0,
        ETB: 154.0,
        EUR: 0.86,
        GBP: 0.75,
        CAD: 1.38,
        AUD: 1.51,
        JPY: 156.0,
        NGN: 1450.0,
        KES: 129.0,
        ZAR: 17.0
      });
    } finally {
      setLoading(false);
    }
  }, [BACKEND_URL]);

  // Fetch rates on mount and every hour
  useEffect(() => {
    fetchExchangeRates();
    
    // Refresh rates every hour
    const interval = setInterval(fetchExchangeRates, 60 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [fetchExchangeRates]);

  /**
   * Convert amount from one currency to another
   * @param {number} amount - Amount to convert
   * @param {string} fromCurrency - Source currency code
   * @param {string} toCurrency - Target currency code
   * @returns {number} Converted amount
   */
  const convertCurrency = useCallback((amount, fromCurrency, toCurrency) => {
    if (!amount || amount === 0) return 0;
    if (fromCurrency === toCurrency) return amount;
    
    const fromRate = exchangeRates[fromCurrency] || 1;
    const toRate = exchangeRates[toCurrency] || 1;
    
    // Convert via USD (base currency)
    const rate = toRate / fromRate;
    return amount * rate;
  }, [exchangeRates]);

  /**
   * Format currency with proper symbol and conversion
   * @param {number} amount - Amount to format
   * @param {string} baseCurrency - Currency the amount is stored in (default: plan base currency)
   * @param {string} targetCurrency - Currency to display (default: current display currency)
   * @param {boolean} compact - Use compact notation (K, M, B)
   * @returns {string} Formatted currency string
   */
  const formatCurrency = useCallback((amount, baseCurrency = null, targetCurrency = null, compact = false) => {
    const fromCurrency = baseCurrency || planBaseCurrency;
    const toCurrency = targetCurrency || displayCurrency;
    
    // Convert amount if currencies differ
    let displayAmount = amount;
    if (fromCurrency !== toCurrency) {
      displayAmount = convertCurrency(amount, fromCurrency, toCurrency);
    }
    
    // Format using Intl.NumberFormat
    const options = {
      style: 'currency',
      currency: toCurrency,
      minimumFractionDigits: 2,  // Always show 2 decimal places for proper currency display
      maximumFractionDigits: 2
    };
    
    if (compact && Math.abs(displayAmount) >= 1000) {
      options.notation = 'compact';
      options.compactDisplay = 'short';
      options.minimumFractionDigits = 0;  // For compact, no decimals
      options.maximumFractionDigits = 0;
    }
    
    return new Intl.NumberFormat('en-US', options).format(displayAmount);
  }, [planBaseCurrency, displayCurrency, convertCurrency]);

  /**
   * Get exchange rate between two currencies
   * @param {string} fromCurrency - Source currency
   * @param {string} toCurrency - Target currency
   * @returns {number} Exchange rate
   */
  const getExchangeRate = useCallback((fromCurrency, toCurrency) => {
    if (fromCurrency === toCurrency) return 1;
    
    const fromRate = exchangeRates[fromCurrency] || 1;
    const toRate = exchangeRates[toCurrency] || 1;
    
    return toRate / fromRate;
  }, [exchangeRates]);

  /**
   * Update the display currency (what the user wants to see)
   */
  const updateDisplayCurrency = useCallback((currency) => {
    setDisplayCurrency(currency);
    localStorage.setItem('displayCurrency', currency);
    console.log(`[CURRENCY] Display currency updated to: ${currency}`);
  }, []);

  /**
   * Update the plan base currency (what currency values are stored in)
   */
  const updatePlanBaseCurrency = useCallback((currency) => {
    setPlanBaseCurrency(currency);
    console.log(`[CURRENCY] Plan base currency updated to: ${currency}`);
  }, []);

  const value = {
    displayCurrency,
    planBaseCurrency,
    exchangeRates,
    loading,
    lastFetched,
    convertCurrency,
    formatCurrency,
    getExchangeRate,
    updateDisplayCurrency,
    updatePlanBaseCurrency,
    refreshRates: fetchExchangeRates
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};
